import { ReactComponent as FlightPlanActive } from '@assets/components/flightPlan/flightPlan-active.svg';
import { IcAltitude20 } from '@assets/icons/20x20';
import { IcPin } from '@assets/icons/32x32';
import { Box, Center, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { useMapContext } from '@components/map/MapContext';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectAltitudeUnit, selectHeightSuffix } from '@redux/settings/settings.selectors';
import { centerOfMass } from '@turf/turf';
import { formatDate } from '@utils/date.utils';
import { convertDistanceUnitFromMeter, formatUnit } from '@utils/unit.utils';
import { toCoordinates } from '@utils/validation/coordinates.utils';
import { format } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import { useMap } from 'react-map-gl/mapbox';

import { DateTimeEnum } from '@/types/dateTime.types';
import { InfoDataTypeEnum, PopupTypeEnum } from '@/types/popup.types';
import { AbstractFlightPlan, Activation, FlightPlanActivationStateEnum } from '@/types/utm/flightPlan.types';

type Props = {
  flightPlan: AbstractFlightPlan;
  activation: Activation;
  position: { x: number; y: number };
  isSmall?: boolean;
  isArchived?: boolean;
  onClose?: () => void;
};

export default function FlightPlanCard({
  flightPlan,
  activation,
  position,
  isArchived = false,
  onClose,
}: Readonly<Props>) {
  const { current: map } = useMap();
  const { mapId } = useMapContext();
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const showHeightSuffix = useAppSelector(selectHeightSuffix);
  const altitudeUnit = useAppSelector(selectAltitudeUnit);
  const isActive = activation.state === FlightPlanActivationStateEnum.ACTIVE;
  const formattedStartTime = format(new Date(activation.startDate), "HH':'mm'");
  const formattedEndTime = format(new Date(activation.endDate), "HH':'mm'");
  const formattedStartDate = formatDate(activation.startDate, DateTimeEnum.DATE);
  const formattedEndDate = formatDate(activation.endDate, DateTimeEnum.DATE);

  const dateLabel = formattedStartDate === formattedEndDate ? 'dateAndTime' : 'multipleDay';

  function handleFlightPlanPopup() {
    onClose && onClose();
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.INFO,
        position,
        data: {
          type: InfoDataTypeEnum.INFO_FLIGHT_PLAN,
          featureValue: flightPlan,
          activation,
        },
      },
    });
  }

  return (
    <HStack width="100%" gap={0.25} align="stretch" flexShrink={0} height={{ base: '112px', '4k': '128px' }}>
      <HStack gap="standardS" backgroundColor="neutral.700" paddingX="standardM" width="100%">
        {isActive ? (
          <Icon as={FlightPlanActive} width="8px" height="86px" />
        ) : (
          <Box width="4px" height="82px" backgroundColor="neutral.600" marginX={0.25} />
        )}
        <VStack gap="standardXS" width="100%">
          <HStack justifyContent="space-between" width="100%">
            <Text
              paddingX="tightM"
              paddingY="tightXS"
              backgroundColor="neutral.black"
              textStyle="screenStd"
              fontWeight="medium"
            >
              {flightPlan.externalId ?? flightPlan.name}
            </Text>
            {!isArchived && (
              <Text
                textStyle="screenStd"
                fontWeight="medium"
                paddingX="tightM"
                paddingY="tightXS"
                color={isActive ? 'green.500' : 'neutral.300'}
                backgroundColor="neutral.900"
              >
                <FormattedMessage id={`flightPlan.activation.state.${activation.state}`} />
              </Text>
            )}
          </HStack>
          <HStack justifyContent="space-between" width="100%">
            <Text color="neutral.300" textStyle="screenStd" whiteSpace="pre-line">
              <FormattedMessage
                id={`flightPlan.${dateLabel}`}
                values={{
                  startDate: (
                    <Text as="span" key="formattedStartDate" textStyle="screenStd" color="neutral.white">
                      {formattedStartDate}
                    </Text>
                  ),
                  startTime: (
                    <Text as="span" key="formattedStartTime" textStyle="screenStd" color="neutral.white">
                      {formattedStartTime}
                    </Text>
                  ),
                  endTime: (
                    <Text as="span" key="formattedEndTime" textStyle="screenStd" color="neutral.white">
                      {formattedEndTime}
                    </Text>
                  ),
                  endDate: (
                    <Text as="span" key="formattedEndDate" textStyle="screenStd" color="neutral.white">
                      {formattedEndDate}
                    </Text>
                  ),
                }}
              />
            </Text>
            <HStack gap={1} alignSelf="start">
              <Icon as={IcAltitude20} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.300" />
              <Text textStyle="screenStd" color="neutral.500" fontWeight="medium">
                <FormattedMessage id="global.max" />
              </Text>
              <Text textStyle="screenStd" fontWeight="medium">
                {formatUnit(altitudeUnit, convertDistanceUnitFromMeter(altitudeUnit, flightPlan.altitudeMax))}
              </Text>
              {showHeightSuffix && (
                <Text textStyle="screenStd" color="neutral.500">
                  <FormattedMessage id="target.amsl" />
                </Text>
              )}
            </HStack>
          </HStack>
        </VStack>
      </HStack>
      <Center
        className="group"
        cursor="pointer"
        width={{ base: '46px', '4k': '54px' }}
        flexShrink={0}
        backgroundColor="neutral.700"
        onClick={() => {
          const center =
            flightPlan.startPosition ?? toCoordinates(centerOfMass(flightPlan.polygons).geometry.coordinates)!;
          map?.getMap().flyTo({ center: [center.longitude, center.latitude], zoom: 14 });
          handleFlightPlanPopup();
        }}
      >
        <Icon
          color="neutral.300"
          as={IcPin}
          width={{ base: '24px', '4k': '32px' }}
          _groupHover={{ color: 'skyBlue' }}
        />
      </Center>
    </HStack>
  );
}
